<template>
  <div
    v-if="bundlePreviewData?.id"
    class="form-sec form-sec--preview"
  >
    <p
      class="mt-para mt-para--preview-message is-flex is-flex--align-center is-flex--justify-center"
      :class="[previewMessage.show ? 'is-visible' : '']"
    >
      {{ previewMessage.message }}
    </p>
    <div
      v-if="computedModelValue"
      class="mt-backdrop"
      @click="handleBackdropClick"
    />
    <div
      v-if="computedModelValue"
      class="mt-modal mt-modal--preview"
    >
      <button
        class="mt-button mt-button--white mt-button--close is-flex is-flex--align-center is-flex--justify-center is-circle"
        @click="closeHandler(false)"
      >
        <close-icon />
      </button>
      <div class="mt-modal__body">
        <div class="mt-modal__header is-flex is-flex--align-center is-flex--justify-between">
          <div class="is-flex is-flex--align-center">
            <button
              class="mt-button mt-button--back is-ghost"
              @click="handleBackdropClick"
            >
              <back-icon />
            </button>
            <h2 class="mt-e-title">
              <span>{{ $t('pages.bundle.draft.bundle_preview.main_title') }}</span>
            </h2>
          </div>
          <div class="mt-button-group is-flex">
            <button
              class="mt-button mt-button--edit mt-button--blue is-bordered is-small"
              @click="closeHandler(false)"
            >
              {{ $t('pages.bundle.draft.edit') }}
            </button>
            <button
              class="mt-button mt-button--publish mt-button--blue is-small"
              @click="$emit('click-publish')"
            >
              {{ $t('pages.bundle.draft.publish') }}
            </button>
          </div>
        </div>
        <div
          v-if="savingDraft" 
          class="wrapper mt-loader-wrapper"
        >
          <animation-processing
            fill="new-blue"
            class="pageSpinner"
            data-qa-id="loading-icon"
          />
        </div>
        <div
          v-else
          ref="previewDraftModalContainer"
          class="mt-modal__content"
        >
          <div class="mt-slider-wrapper mt-modal__content-wrapper">
            <div
              v-if="!isEmpty(bundlePreviewData.assets.main)"
              class="mt-slider"
            >
              <div class="mt-slider__content mt-slider__content-slides">
                <Carousel
                  ref="mainImageCarousel"
                  v-model="carouselConfigs.currentSlide"
                  :items-to-show="carouselConfigs.itemsToShow"
                  :items-to-scroll="carouselConfigs.itemsToScroll"
                  :breakpoints="carouselConfigs.breakpoints"
                >
                  <Slide
                    v-for="mainImage in bundlePreviewData.assets.main"
                    :key="mainImage.id"
                  >
                    <figure class="mt-slider__img">
                        <img
                        :src="mainImage.resourceLocation"
                        alt="main"
                        >
                    </figure>
                  </Slide>
                </Carousel>
                <div class="mt-button-group is-flex is-flex--align-center">
                  <button
                    v-if="carouselConfigs.currentSlide >= 1"
                    class="mt-button mt-button--prev mt-button--black is-circle is-flex is-flex--align-center is-flex--justify-center"
                    @click="slideToPrev"
                  >
                    <ArrowBack />
                  </button>
                  <button
                    v-if="showNextButton"
                    class="mt-button mt-button--next mt-button--black is-circle is-flex is-flex--align-center is-flex--justify-center"
                    @click="slideToNext"
                  >
                    <ArrowBack />
                  </button>
                </div>
              </div>
            </div>
            <div
              class="mt-slider__content mt-slider__content-details is-flex is-flex--align-center is-flex--justify-between"
            >
              <div>
                <h3
                  v-tooltip="{
                    content: bundlePreviewData.name,
                    placement: 'bottom',
                    disabled: bundlePreviewData.name.length <= PREVIEW_DRAFT_CONSTANTS.max_truncate_string_length }"
                  class="mt-e-title"
                >
                  {{ Misc.truncate(bundlePreviewData.name, PREVIEW_DRAFT_CONSTANTS.max_truncate_string_length) }}
                </h3>
                <p class="mt-e-para mt-e-para--basic-information">
                  {{ $t('pages.bundle.draft.by') }}
                  <span
                    v-if="bundlePreviewData?.foundries?.length > 1"
                    v-tooltip="{ content: showBundleFoundries(bundlePreviewData.foundries), placement: 'bottom' }"
                  >
                    {{ `${bundlePreviewData.foundries.length} ${$t('pages.bundle.draft.bundle_list.table.foundries')}` }}
                  </span>
                  <span v-else-if="bundlePreviewData?.foundries?.length"> {{ (bundlePreviewData.foundries[0].name ?? bundlePreviewData.foundries[0].label) }}</span>
                  <DotIcon class="mt-icon mt-icon--dot-icon" />
                  {{ bundlePreviewData.collection.length }}
                  {{ $t('pages.bundle.draft.bundle_preview.styles') }}
                </p>
              </div>
              <div class="is-flex is-flex--align-center is-hidden--mobile">
                <p class="mt-e-para mt-e-para--bundle-price">
                  {{ bundlePreviewData.collection.length }} {{ $t('pages.bundle.draft.bundle_preview.styles') }}
                  {{ $t('pages.bundle.draft.bundle_preview.for') }} ${{ bundlePrice?.price }}
                </p>
                <button
                  class="mt-button mt-button--blue is-small"
                  @click="showPreviewModeIsOn()"
                >
                  {{ $t('pages.bundle.draft.bundle_preview.get_this_bundle') }}
                </button>
              </div>
            </div>
          </div>
          <div class="mt-description mt-modal__content-wrapper">
            <p
              class="mt-e-para"
              v-html="bundlePreviewData?.description?.[0]?.description"
            />
          </div>

          <div class="mt-fonts mt-modal__content-wrapper">
            <div
              class="mt-typetester is-flex is-flex--align-center is-flex--justify-between"
              @click="showPreviewModeIsOn()"
            >
              <div class="mt-typetester__content mt-typetester__content-typefield">
                <img
                  class="mt-img is-hidden--tablet"
                  :src="draftTyperFieldImage"
                  alt="draft toggler"
                >
                <img
                  class="mt-img is-hidden is-hidden--mobile"
                  :src="draftTyperFieldImageTablet"
                  alt="draft toggler"
                >
                <img
                  class="mt-img is-hidden"
                  :src="draftTyperFieldImageMobile"
                  alt="draft toggler"
                >
              </div>
              <div class="mt-typetester__content mt-typetester__content-properties">
                <img
                  class="mt-img is-hidden--tablet"
                  :src="draftPropertiesImage"
                  alt="draft toggler"
                >
                <img
                  class="mt-img is-hidden is-hidden--mobile"
                  :src="draftPropertiesImageTablet"
                  alt="draft toggler"
                >
                <img
                  class="mt-img is-hidden"
                  :src="draftPropertiesImageMobile"
                  alt="draft toggler"
                >
              </div>
            </div>
            <div id="selected-families">
              <div
                v-for="family in chunkedData"
                :key="family.familyId"
                class="mt-fonts__content"
                @click="showPreviewModeIsOn()"
              >
                <div
                  class="is-flex is-flex--justify-between is-flex--align-center"
                  data-qa-id="single-font-items"
                >
                  <div class="mt-fonts__meta-data is-flex is-flex--align-center">
                    <div class="mt-fonts__title is-flex is-flex--align-center">
                      <MFPIcon
                        data-qa-id="monotype-icon"
                        class-name="mt-fonts__logo"
                      />
                      <span
                        v-tooltip="{
                          content: family.familyName,
                          placement: 'bottom',
                          disabled: family.familyName.length <= PREVIEW_DRAFT_CONSTANTS.family_name_truncate_length }"
                        data-qa-id="family-name"
                        class="mt-fonts__family-name"
                      >
                        {{ Misc.truncate(family.familyName, PREVIEW_DRAFT_CONSTANTS.family_name_truncate_length) }}
                      </span>
                    </div>

                    <div class="mt-fonts__details is-flex is-flex--align-center">
                      <span class="mt-fonts__style-count">{{ family.styleCount }}
                        {{
                          family.styleCount <= 1
                            ? $t('pages.bundle.draft.bundle_preview.style')
                            : $t('pages.bundle.draft.bundle_preview.styles')
                        }}
                      </span>

                      <div class="mt-separator" />
                      <div class="mt-fonts__foundry-name">
                        {{ $t('pages.bundle.draft.by') }} <span data-qa-id="vendor-name">{{ family.vendorName }} </span>
                      </div>
                    </div>
                  </div>
                </div>
                <FontPreviewer
                  :src="getFontRenderSrc({ md5: family?.md5, name: family.familyName })"
                  :family-name="family.familyName"
                />
              </div>
            </div>
          </div>

          <div ref="inspirationGalleryRef">
            <div
              v-if="!isEmpty(bundlePreviewData.assets.inspirational)"
              class="mt-inspiration mt-modal__content-wrapper"
            >
              <h2 class="mt-e-title">
                {{ $t('pages.bundle.draft.bundle_preview.inspiration') }}
              </h2>
              <div class="mt-inspiration__content">
                <figure
                  v-for="inspirationImage in bundlePreviewData.assets.inspirational"
                  :key="inspirationImage.id"
                  class="mt-inspiration__img"
                >
                  <img
                    :src="inspirationImage.resourceLocation"
                    alt="inspirational"
                  >
                </figure>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="./script/index" />

<style lang="scss">
@import 'mpl-css/lib/assets/global/functions/index';
@import 'mpl-css/lib/assets/global/variables/fonts';
@import 'mpl-css/lib/assets/global/variables/colors';
@import 'mpl-css/lib/assets/global/variables/breakpoints';

.wrapper {
  display: grid;
  align-self: center;
  margin-top: auto;
  justify-items: center;
}

.pageSpinner {
  width: 3.4rem;
  height: auto;
}

.is-hidden {
  display: none;

  @media screen and (max-width: 719px) {
    display: block;
  }

  &--tablet {

    @media screen and (max-width: 1279px) {
      display: none;
    }
  }

  &--mobile {

    @media screen and (max-width: 1279px) {
      display: block;
    }

    @media screen and (max-width: 719px) {
      display: none;
    }
  }
}

.form-sec {

  &.form-sec--preview {
    padding: 0;
    border-bottom: 0;

    .mt-para--preview-message {
      position: fixed;
      top: rem(48);
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1002;
      background-color: rgba($color: map-get($colors, 'white'), $alpha: 0.9);
      color: map-get($colors, 'spirits--600');
      font-size: rem(24);
      font-family: $mt-medium;
      opacity: 0;
      visibility: hidden;
      transition: all 300ms ease-in-out;

      &.is-visible {
        opacity: 1;
        visibility: visible;
        transition-duration: 150ms;
      }
    }

    .mt-backdrop {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: rgba($color: map-get($colors, 'black'), $alpha: 0.24);
      z-index: 1000;
    }

    .mt-modal {

      &--preview {
        position: fixed;
        top: rem(48);
        right: 0;
        left: 0;
        z-index: 1000;
        bottom: 0;
        background-color: map-get($colors, 'white');

        .mt-button--close {
          position: fixed;
          top: rem(8);
          right: rem(8);
          line-height: 1;
          width: rem(32);
          height: rem(32);
          padding: 0;
          background-color: map-get($colors, 'white');

          svg {
            width: rem(16);
            height: rem(16);

            path {
              fill: map-get($colors, 'spirits--800');
            }
          }
        }

        .mt-modal__body {
          padding: 0 rem(24);
          height: 100%;
          background-color: map-get($colors, 'spirits--100');

          @media screen and (max-width: 719px) {
            padding: 0;
          }

          .mt-modal__header {
            padding: rem(24);
            margin: 0 rem(-24) rem(24);
            background-color: map-get($colors, 'white');
            border-bottom: 1px solid map-get($colors, 'spirits--300');

            @media screen and (max-width: 719px) {
              margin: 0;
            }

            .is-flex {
              gap: rem(16);
            }

            .mt-e-title {
              margin-bottom: 0;
            }

            .mt-button-group {
              gap: rem(8);

              @media screen and (max-width: 719px) {
                position: fixed;
                right: 0;
                bottom: 0;
                left: 0;
                background-color: map-get($colors, 'white');
                padding: rem(16);
                box-shadow: 0 0 rem(10) rgba($color: map-get($colors, 'black'), $alpha: 0.15);
                z-index: 3;

                .mt-button {
                  font-size: rem(11);
                  line-height: rem(16);
                  padding: 0;
                  height: rem(32);

                  &--edit {
                    width: rem(57);
                  }

                  &--publish {
                    width: calc(100% - 65px);
                  }
                }
              }
            }
          }

          .mt-modal__content {
            overflow-y: scroll;
            height: calc(100% - 113px);
            background-color: map-get($colors, 'white');
            border-top-left-radius: rem(16);
            border-top-right-radius: rem(16);

            @media screen and (max-width: 1279px) {
              border-top-left-radius: rem(8);
              border-top-right-radius: rem(8);
            }

            &-wrapper {
              padding: rem(24);
              border-bottom: 1px solid map-get($colors, 'spirits--300');

              &:last-child {
                border-bottom: 0;
              }

              .mt-e-para {
                margin-bottom: 0;
                &--basic-information {
                  display: flex;
                  align-items: center;

                  span {
                    margin-left: rem(4);
                  }
                }
                .mt-icon--dot-icon {
                  width: rem(4);
                  height: rem(4);
                  margin-left: rem(8);
                  margin-right: rem(8);
                }
              }

              .mt-slider {
                overflow-x: hidden;
                margin-right: rem(-24);
                margin-bottom: rem(24);

                .carousel__slide {
                  margin: 0 rem(8);
                }

                &__content {

                  &-slides {
                    position: relative;

                    .mt-slider__img {
                      width: 100%;
                      margin: rem(0);
                      border-radius: rem(16);
                      overflow: hidden;

                      img {
                        width: 100%;
                      }
                    }

                    .mt-button-group {
                      position: absolute;
                      top: 50%;
                      right: rem(16);
                      left: rem(16);
                      transform: translateY(-50%);

                      .mt-button {
                        width: rem(40);
                        height: rem(40);
                        font-size: 0;
                        display: inline-flex;

                        &--prev {
                          margin-right: auto;
                          margin-left: 0;
                        }

                        &--next {
                          transform: rotate(180deg);
                          margin-left: auto;
                          margin-right: 0;
                        }

                        svg {
                          width: rem(16);
                          height: rem(16);

                          path {
                            fill: map-get($colors, 'white');
                          }
                        }
                      }
                    }
                  }

                  &-details {

                    @media screen and (max-width: 719px) {
                      align-items: flex-start;

                      &>.is-flex {
                        display: none;
                      }
                    }

                    .mt-e-title {
                      font-size: rem(23);
                      line-height: rem(32);
                      margin-bottom: rem(8);
                      word-break: break-all;
                    }

                    .is-flex {
                      gap: rem(16);
                    }

                    .mt-e-para {
                      &--bundle-price {
                        font-family: $mt-medium;
                        color: map-get($colors, 'spirits--900');
                        font-weight: 500;
                      }
                    }
                  }
                }
              }

              .mt-typetester {
                border-bottom: 1px solid map-get($colors, 'spirits--300');

                @media screen and (max-width: 719px) {
                  flex-direction: column;
                }

                &__content {

                  &-typefield {
                    height: rem(32);
                    padding-left: rem(24);

                    @media screen and (max-width: 1279px) {
                      width: rem(302);
                    }

                    @media screen and (max-width: 719px) {
                      width: 100%;
                      padding: 0 rem(24);
                      margin: rem(8) 0;
                    }

                    @media screen and (max-width: 481px) {
                      height: auto;
                    }
                  }

                  &-properties {
                    height: rem(56);

                    @media screen and (max-width: 1279px) {
                      width: rem(302);
                      height: rem(48);
                      padding-right: rem(24);

                      @media screen and (max-width: 719px) {
                        width: 100%;
                        padding: 0 rem(24);
                        border-top: 1px solid map-get($colors, 'spirits--300');
                      }

                      @media screen and (max-width: 481px) {
                        height: auto;
                      }
                    }
                  }

                  img {
                    height: 100%;

                    @media screen and (max-width: 1279px) {
                      width: 100%;
                      height: auto;
                    }

                    @media screen and (max-width: 719px) {
                      width: auto;
                      height: 100%;
                      margin: 0;
                    }

                    @media screen and (max-width: 481px) {
                      width: 100%;
                      height: auto;
                    }
                  }
                }
              }

              &.mt-fonts {
                padding: 0;
                border-bottom: 0;

                .mt-fonts__content {
                  padding: rem(24);
                  border-bottom: 1px solid map-get($colors, 'spirits--300');

                  .mt-fonts__meta-data {
                    color: map-get($colors, 'spirits--700');
                    padding: rem(12) 0;
                    font-size: rem(13);
                    line-height: rem(16);

                    .mt-fonts__title {
                      gap: rem(8);

                      .mt-fonts__logo {
                        line-height: 0;
                      }

                      .mt-fonts__family-name {
                        font-family: $mt-medium;
                        font-weight: 500;
                      }
                    }

                    .mt-fonts__style-count {
                      margin-left: rem(16);
                    }

                    .mt-separator {
                      position: relative;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      margin: 0 rem(8);

                      &::after {
                        position: absolute;
                        content: '';
                        height: rem(2);
                        width: rem(2);
                        background: #576579;
                      }
                    }
                  }

                  .fou-add-family-imgage-wrap {
                    padding: rem(16) 0 0;

                    img {
                      width: auto;
                      height: rem(40);

                      @media screen and (max-width: 719px) {
                        width: 100%;
                        height: auto;
                      }
                    }
                  }
                }
              }

              &.mt-inspiration {

                .mt-e-title {
                  margin: 0 rem(-24) rem(24);
                  padding: 0 rem(24) rem(24);
                  border-bottom: 1px solid map-get($colors, 'spirits--300');
                }

                .mt-inspiration__content {
                  display: grid;
                  grid-template-columns: repeat(2, 1fr);
                  grid-template-rows: masonry;
                  grid-auto-flow: dense;
                  gap: rem(24);

                  @media screen and (max-width: 719px) {
                    grid-template-columns: repeat(1, 1fr);
                  }

                  .mt-inspiration__img {
                    overflow: hidden;
                    border-radius: rem(16);
                    margin: 0;

                    img {
                      max-width: 100%;
                      object-fit: cover;
                      object-position: center;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

body.is-unscrollable.is-create-bundle-draft {
  overflow: hidden !important;
}
</style>
